import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Lock,
  Database,
  Mail,
  CreditCard,
  Eye,
  UserCheck,
  Globe,
  FileText,
  Phone
} from 'lucide-react';

const PrivacyPage = () => {
  const { i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-20">

      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16">

          <h1
            className="text-4xl md:text-5xl font-serif text-[#F5F2EB] mb-4"
            data-testid="privacy-heading"
          >
            {isHindi
              ? 'गोपनीयता नीति'
              : 'Privacy Policy'}
          </h1>

          <p className="text-[#C9A84C] text-lg tracking-wide">
            {isHindi
              ? 'आपकी जानकारी की सुरक्षा हमारी प्राथमिकता है'
              : 'Your Privacy & Confidentiality Matters to Us'}
          </p>

        </div>

        {/* Intro */}
        <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8 mb-10">

          <div className="flex items-center gap-4 mb-5">
            <Shield className="text-[#C9A84C]" size={28} />

            <h2 className="text-2xl font-serif text-[#F5F2EB]">
              {isHindi
                ? 'हम आपकी गोपनीयता का सम्मान करते हैं'
                : 'We Respect Your Privacy'}
            </h2>
          </div>

          <p className="text-[#A3A3A3] leading-relaxed text-lg">
            {isHindi
              ? 'यह गोपनीयता नीति बताती है कि हमारी ज्योतिष एवं वास्तु परामर्श सेवाओं का उपयोग करते समय आपकी व्यक्तिगत जानकारी कैसे एकत्रित, उपयोग, सुरक्षित एवं संरक्षित की जाती है। हम आपकी जन्म जानकारी, व्यक्तिगत विवरण एवं परामर्श से संबंधित सभी डेटा को अत्यंत गोपनीय मानते हैं।'
              : 'This Privacy Policy explains how your personal information is collected, used, stored, and protected when you use our astrology and vastu consultation services. We consider your birth details, consultation records, and personal information extremely confidential and sensitive.'}
          </p>

        </div>

        {/* Sections */}
        <div className="space-y-8">

          {/* Data Collection */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <Database className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'हम कौन सी जानकारी एकत्र करते हैं'
                  : 'Information We Collect'}
              </h2>
            </div>

            <ul className="space-y-4 text-[#A3A3A3] leading-relaxed">

              <li>
                • {isHindi
                  ? 'नाम, मोबाइल नंबर, ईमेल पता एवं संपर्क जानकारी'
                  : 'Name, phone number, email address, and contact details'}
              </li>

              <li>
                • {isHindi
                  ? 'जन्म तिथि, जन्म समय एवं जन्म स्थान जैसी ज्योतिषीय जानकारी'
                  : 'Birth date, birth time, and place of birth required for astrological analysis'}
              </li>

              <li>
                • {isHindi
                  ? 'घर, कार्यालय या संपत्ति से संबंधित वास्तु विवरण'
                  : 'Property details, floor plans, or vastu-related information for consultation'}
              </li>

              <li>
                • {isHindi
                  ? 'ऑनलाइन भुगतान एवं बुकिंग से संबंधित जानकारी'
                  : 'Information related to bookings and online payments'}
              </li>

              <li>
                • {isHindi
                  ? 'परामर्श के दौरान साझा की गई व्यक्तिगत या आध्यात्मिक जानकारी'
                  : 'Personal or spiritual information voluntarily shared during consultations'}
              </li>

            </ul>

          </div>

          {/* Usage */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <Eye className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'आपकी जानकारी का उपयोग कैसे किया जाता है'
                  : 'How We Use Your Information'}
              </h2>
            </div>

            <ul className="space-y-4 text-[#A3A3A3] leading-relaxed">

              <li>
                • {isHindi
                  ? 'सटीक ज्योतिषीय एवं वास्तु परामर्श प्रदान करने हेतु'
                  : 'To provide accurate astrology and vastu consultations'}
              </li>

              <li>
                • {isHindi
                  ? 'कुंडली विश्लेषण, विवाह मिलान एवं भविष्यवाणी हेतु'
                  : 'For horoscope analysis, marriage matching, and predictions'}
              </li>

              <li>
                • {isHindi
                  ? 'ऑनलाइन अपॉइंटमेंट एवं भुगतान प्रबंधन हेतु'
                  : 'To manage appointments, bookings, and payments'}
              </li>

              <li>
                • {isHindi
                  ? 'ग्राहक सहायता एवं सेवाओं में सुधार हेतु'
                  : 'To improve our services and customer support'}
              </li>

              <li>
                • {isHindi
                  ? 'आध्यात्मिक मार्गदर्शन एवं व्यक्तिगत सलाह प्रदान करने हेतु'
                  : 'To provide spiritual guidance and personalized recommendations'}
              </li>

            </ul>

          </div>

          {/* Confidentiality */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <Lock className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'डेटा गोपनीयता एवं सुरक्षा'
                  : 'Data Confidentiality & Security'}
              </h2>
            </div>

            <p className="text-[#A3A3A3] leading-relaxed mb-5">
              {isHindi
                ? 'हम आपकी व्यक्तिगत जानकारी एवं जन्म विवरण को अत्यंत गोपनीय रखते हैं। आपकी जानकारी कभी भी किसी तीसरे पक्ष को बेची, किराए पर दी या साझा नहीं की जाती।'
                : 'We maintain strict confidentiality regarding your personal information and birth details. Your data is never sold, rented, or disclosed to unauthorized third parties.'}
            </p>

            <ul className="space-y-4 text-[#A3A3A3] leading-relaxed">

              <li>
                • {isHindi
                  ? 'सुरक्षित सर्वर एवं एन्क्रिप्टेड सिस्टम का उपयोग'
                  : 'Use of secure servers and encrypted systems'}
              </li>

              <li>
                • {isHindi
                  ? 'परामर्श डेटा तक सीमित एवं अधिकृत पहुँच'
                  : 'Restricted access to consultation records'}
              </li>

              <li>
                • {isHindi
                  ? 'जन्म कुंडली एवं वास्तु रिपोर्ट की पूर्ण गोपनीयता'
                  : 'Confidential handling of horoscope and vastu reports'}
              </li>

              <li>
                • {isHindi
                  ? 'कानूनी आवश्यकता होने पर ही सीमित जानकारी साझा की जा सकती है'
                  : 'Information may only be shared if legally required by authorities'}
              </li>

            </ul>

          </div>

          {/* Payment */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <CreditCard className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'भुगतान सुरक्षा'
                  : 'Payment Security'}
              </h2>
            </div>

            <p className="text-[#A3A3A3] leading-relaxed">
              {isHindi
                ? 'सभी ऑनलाइन भुगतान विश्वसनीय एवं सुरक्षित पेमेंट गेटवे जैसे Razorpay आदि के माध्यम से प्रोसेस किए जाते हैं। हम आपके डेबिट/क्रेडिट कार्ड या बैंकिंग जानकारी को अपने सर्वर पर संग्रहित नहीं करते।'
                : 'All online payments are processed through trusted and secure payment gateways such as Razorpay. We do not store your debit card, credit card, or banking information on our servers.'}
            </p>

          </div>

          {/* User Rights */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <UserCheck className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'उपयोगकर्ता अधिकार'
                  : 'User Rights'}
              </h2>
            </div>

            <ul className="space-y-4 text-[#A3A3A3] leading-relaxed">

              <li>
                • {isHindi
                  ? 'आप अपनी व्यक्तिगत जानकारी को अपडेट या संशोधित करने का अनुरोध कर सकते हैं'
                  : 'You may request correction or updates to your personal information'}
              </li>

              <li>
                • {isHindi
                  ? 'आप अपने डेटा को हटाने का अनुरोध कर सकते हैं'
                  : 'You may request deletion of your personal data'}
              </li>

              <li>
                • {isHindi
                  ? 'आप ईमेल के माध्यम से अपनी गोपनीयता संबंधी चिंताओं को साझा कर सकते हैं'
                  : 'You may contact us regarding any privacy concerns'}
              </li>

            </ul>

          </div>

          {/* Legal */}
          <div className="bg-[#111111] border border-[#C9A84C]/10 rounded-3xl p-8">

            <div className="flex items-center gap-4 mb-6">
              <FileText className="text-[#C9A84C]" size={24} />

              <h2 className="text-2xl font-serif text-[#F5F2EB]">
                {isHindi
                  ? 'कानूनी अनुपालन'
                  : 'Legal Compliance'}
              </h2>
            </div>

            <p className="text-[#A3A3A3] leading-relaxed">
              {isHindi
                ? 'यह गोपनीयता नीति भारतीय सूचना प्रौद्योगिकी अधिनियम 2000 तथा डिजिटल पर्सनल डेटा प्रोटेक्शन अधिनियम (DPDP Act 2023) के अनुसार संचालित की जाती है।'
                : 'This Privacy Policy is governed in accordance with the Information Technology Act, 2000 and the Digital Personal Data Protection (DPDP) Act, 2023 of India.'}
            </p>

          </div>

          {/* Contact */}
          <div className="bg-gradient-to-r from-[#1A1A1A] to-[#111111] border border-[#C9A84C]/20 rounded-3xl p-8 text-center">

            <Mail className="text-[#C9A84C] mx-auto mb-5" size={34} />

            <h2 className="text-3xl font-serif text-[#F5F2EB] mb-4">
              {isHindi
                ? 'हमसे संपर्क करें'
                : 'Contact Us'}
            </h2>

            <p className="text-[#A3A3A3] mb-6 leading-relaxed">
              {isHindi
                ? 'यदि आपको हमारी गोपनीयता नीति से संबंधित कोई प्रश्न, सुझाव या डेटा हटाने का अनुरोध करना हो, तो कृपया हमसे संपर्क करें।'
                : 'If you have any questions, concerns, or requests regarding this Privacy Policy or your data, please feel free to contact us.'}
            </p>

            <div className="space-y-3 text-[#D4D4D4]">

              <div className="flex items-center justify-center gap-3">
                <Mail size={18} className="text-[#C9A84C]" />
                <span>astrologytimes5@gmail.com</span>
              </div>

              <div className="flex items-center justify-center gap-3">
                <Phone size={18} className="text-[#C9A84C]" />
                <span>+91 94406 98250</span>
              </div>

            </div>

          </div>

          {/* Footer Note */}
          <div className="text-center pt-8">

            <p className="text-[#666666] text-sm leading-relaxed">
              {isHindi
                ? 'इस वेबसाइट का उपयोग करके, आप हमारी गोपनीयता नीति एवं नियमों से सहमत होते हैं।'
                : 'By using this website, you agree to our Privacy Policy and terms related to astrology and vastu consultation services.'}
            </p>

          </div>

        </div>

      </div>

    </div>
  );
};

export default PrivacyPage;